import {
  queryOptions,
  mutationOptions,
  QueryOptions,
  MutationOptions,
} from '@tanstack/react-query';
import { getClient } from './ClientRegistry';
import {
  resolveMethodMetadata,
  MethodOptions,
  getCurrentDecoratedMethodName,
} from './APIMethod.decorator';

/**
 * Type for path parameters (e.g., { id: string })
 */
export type PathParams = Record<string, string | number>;

/**
 * Type for query parameters (e.g., { page: number, limit: number })
 */
export type QueryParams = Record<string, string | number | boolean | undefined>;

/**
 * Creates a TanStack Query queryOptions object for GET requests
 * Uses PublicHttpClient from ClientRegistry to make actual HTTP requests
 *
 * @template TData - The response data type
 * @template TPathParams - The path parameters type (e.g., { id: string })
 * @template TQueryParams - The query parameters type (e.g., { page: number })
 * @param instance - The service instance with decorated methods
 * @param params - URL params to replace in path (e.g., { id: '123' } replaces ':id')
 * @param queryParams - Query string parameters to append to URL
 * @param options - Additional query options to override decorator options
 */
export function createFetch<
  TData,
  TPathParams extends PathParams = PathParams,
  TQueryParams extends QueryParams = QueryParams,
>(
  instance: object,
  params?: TPathParams,
  queryParams?: TQueryParams,
  options?: Partial<QueryOptions<TData, Error>>,
) {
  const methodName = getCurrentDecoratedMethodName();

  const { clientClass, httpMethod, fullPath, methodOptions } = resolveMethodMetadata(
    instance,
    methodName,
  );
  const client = getClient(clientClass);

  if (!client) {
    throw new Error(
      `Client '${
        clientClass?.name || clientClass
      }' not registered. Please register it using registerClient().`,
    );
  }

  // Replace path parameters (e.g., :id)
  let finalPath = fullPath;
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      finalPath = finalPath.replace(`:${k}`, String(v));
    });
  }

  // Merge method options with provided options
  const mergedOptions: MethodOptions = { ...methodOptions, ...options };

  return queryOptions({
    queryKey: [clientClass.name, finalPath, params, queryParams],
    queryFn: () =>
      client.request<TData>(finalPath, {
        method: httpMethod,
        params: queryParams as Record<string, string | number>,
      }),
    staleTime: mergedOptions.staleTime,
    gcTime: mergedOptions.cacheTime,
    retry: mergedOptions.retry,
    enabled: mergedOptions.enabled,
    refetchOnWindowFocus: mergedOptions.refetchOnWindowFocus,
    refetchOnReconnect: mergedOptions.refetchOnReconnect,
  });
}

/**
 * Configuration for createCommit to handle query parameters
 */
export interface CommitConfig {
  /**
   * Keys from variables that should be extracted and used as query parameters
   * instead of being sent in the request body.
   *
   * @example
   * // If variables = { accountId: '123', name: 'Team' } and queryParamKeys = ['accountId']
   * // Result: POST /teams?accountId=123 with body { name: 'Team' }
   */
  queryParamKeys?: string[];
}

/**
 * Creates a TanStack Query mutationOptions object for POST/PUT/PATCH/DELETE requests
 * Uses PublicHttpClient from ClientRegistry to make actual HTTP requests
 *
 * @template TData - The response data type
 * @template TVariables - The mutation variables/payload type
 * @param instance - The service instance with decorated methods
 * @param options - Additional mutation options
 * @param config - Configuration for query parameters extraction
 *
 * @example
 * ```typescript
 * // Basic usage
 * @POST<CreateTeamRequest, CreateTeamResponse>('/')
 * createTeam() {
 *   return createCommit<CreateTeamResponse, CreateTeamRequest>(this);
 * }
 *
 * // With query parameters
 * @POST<CreateTeamRequest, CreateTeamResponse>('/')
 * createTeam() {
 *   return createCommit<CreateTeamResponse, CreateTeamRequest>(
 *     this,
 *     undefined,
 *     { queryParamKeys: ['accountId'] }
 *   );
 * }
 * // Variables { accountId: '123', name: 'Team' } becomes:
 * // POST /teams?accountId=123 with body { name: 'Team' }
 * ```
 */
export function createCommit<TData, TVariables extends Record<string, unknown>>(
  instance: object,
  options?: Partial<MutationOptions<TData, Error, TVariables>>,
  config?: CommitConfig,
) {
  const methodName = getCurrentDecoratedMethodName();

  const { clientClass, httpMethod, fullPath, methodOptions } = resolveMethodMetadata(
    instance,
    methodName,
  );
  const client = getClient(clientClass);

  if (!client) {
    throw new Error(
      `Client '${
        clientClass?.name || clientClass
      }' not registered. Please register it using registerClient().`,
    );
  }

  const queryParamKeys = config?.queryParamKeys || [];

  return mutationOptions({
    mutationFn: (variables: TVariables) => {
      let mutationPath = fullPath;
      const queryParams: Record<string, string> = {};
      const bodyVariables: Record<string, unknown> = {};

      // Process variables
      if (variables && typeof variables === 'object') {
        Object.entries(variables).forEach(([k, v]) => {
          // Check if this key is a path parameter
          if (mutationPath.includes(`:${k}`)) {
            mutationPath = mutationPath.replace(`:${k}`, String(v));
          }
          // Check if this key should be a query parameter
          else if (queryParamKeys.includes(k)) {
            if (v !== undefined && v !== null) {
              queryParams[k] = String(v);
            }
          }
          // Otherwise, it goes in the body
          else {
            bodyVariables[k] = v;
          }
        });
      }

      // Append query parameters to path
      if (Object.keys(queryParams).length > 0) {
        const searchParams = new URLSearchParams(queryParams);
        mutationPath = `${mutationPath}?${searchParams.toString()}`;
      }

      const serializedBody = JSON.stringify(bodyVariables);
      const hasBody = serializedBody !== undefined && serializedBody !== '{}';

      return client.request<TData>(mutationPath, {
        method: httpMethod,
        body: httpMethod === 'GET' || !hasBody ? undefined : serializedBody,
      });
    },
    retry: methodOptions.retry,
    ...options,
  });
}
